import { useCallback } from 'react';

import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useCreateRequestEventMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
} from '@/rtk-query.gql';

import { ICONS } from './constants';

type MarkAsCompleteProps = {
  request: RequestFragment & { events: RequestEventFragment[] };
  canManageRequest: (
    request: RequestFragment & { events: RequestEventFragment[] },
  ) => boolean;
};

export const MarkAsComplete = ({
  request,
  canManageRequest,
}: MarkAsCompleteProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createEvent, { isLoading }] = useCreateRequestEventMutation();

  const markAsComplete = useCallback(async () => {
    try {
      await createEvent({
        content: '',
        requestId: request.id,
        type: RequestEventType.COMPLETED,
        refRequestId: null,
      });
      dispatch(
        addToast({
          severity: 'success',
          summary: t('common.success'),
          detail: t('issues.completedIssue'),
          life: ToastLife.DEFAULT,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('issues.errorCompletingIssue') + error,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  }, [createEvent, dispatch, request.id]);

  if (!canManageRequest(request)) {
    return null;
  }

  return (
    <Button
      label={t('common.complete')}
      className="status-button status-button--completed"
      icon={ICONS[RequestEventType.COMPLETED]}
      loading={isLoading}
      disabled={isLoading}
      onClick={markAsComplete}
    />
  );
};
